import { useTranslation } from 'react-i18next';
import { Divider, Tooltip } from '@fluentui/react-components';
import { PersonAdd16Filled, PeopleSwap16Regular } from '@fluentui/react-icons';
import { BasicButton } from '@components/common/BasicButton';
import { useEventActionBarStyles } from './EventActionBar.styles';

export interface EventActionBarProps {
  selectedCount: number;
  /** When true, Claim and Handoff are disabled with a tooltip. */
  actionsDisabled?: boolean;
  /** Tooltip shown on disabled buttons explaining why. */
  actionsDisabledReason?: string;
  onClaimClick: () => void;
  onHandoffClick: () => void;
}

export const EventActionBar: React.FC<EventActionBarProps> = ({
  selectedCount,
  actionsDisabled = false,
  actionsDisabledReason,
  onClaimClick,
  onHandoffClick,
}) => {
  const styles = useEventActionBarStyles();
  const { t } = useTranslation('planner');

  const claimButton = (
    <BasicButton
      variant="subtle"
      size="medium"
      className={styles.actionButton}
      icon={<PersonAdd16Filled />}
      onClick={onClaimClick}
      disabled={actionsDisabled}
      data-testid="action-bar-claim"
    >
      {t('actionBar.claim')}
    </BasicButton>
  );

  const handoffButton = (
    <BasicButton
      variant="subtle"
      size="medium"
      className={styles.actionButton}
      icon={<PeopleSwap16Regular />}
      onClick={onHandoffClick}
      disabled={actionsDisabled}
      data-testid="action-bar-handoff"
    >
      {t('actionBar.handoff')}
    </BasicButton>
  );

  return (
    <div className={styles.root} data-testid="event-action-bar">
      <span className={styles.selectedCount}>
        {t('actionBar.selectedCount', { count: selectedCount })}
      </span>
      <Divider vertical style={{ height: '1.25rem' }} />
      {actionsDisabled && actionsDisabledReason ? (
        <Tooltip content={actionsDisabledReason} relationship="description">
          <span>{claimButton}</span>
        </Tooltip>
      ) : (
        claimButton
      )}
      <Divider vertical style={{ height: '1.25rem' }} />
      {actionsDisabled && actionsDisabledReason ? (
        <Tooltip content={actionsDisabledReason} relationship="description">
          <span>{handoffButton}</span>
        </Tooltip>
      ) : (
        handoffButton
      )}
    </div>
  );
};

EventActionBar.displayName = 'EventActionBar';
